import { memo, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Icon, IconButton, PropertyCard as ConstellationPropertyCard } from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconWarningFilled } from '@zillow/constellation-icons';
import { LoadingTag } from '@zg-rentals/loading-components';

import { RMWContext } from '../../../../providers/RMWContext';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../../../hooks/queries/useGetPropertyDetailsOverview';

import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import EditPropertyTypeMenu from '../../../../components/EditPropertyTypeMenu';
import AddressDetails from '../AddressDetails';

import { propertyPhotoContainerStyles, StyledLink } from './styles';

import type { GetUserPropertyOverviewResponseWebDto } from '@zg-rentals/rental-manager-env';

type Props = {
  listingAlias: string;
  onLoadComplete?: () => void;
};

const PropertyCard = ({ listingAlias, onLoadComplete }: Props) => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const tagSize = isScreenDesktop ? 'md' : 'sm';

  const { data, isLoading: isPropertyDetailsOverviewLoading } = useGetPropertyDetailsOverview(listingAlias);
  const { isLoading: isUseWebAnalyticsLoading, data: webAnalyticsData } =
    useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
      {
        listingAlias,
        includeLeases: false,
      },
      { enabled: Boolean(listingAlias) },
    );

  const propertyDetailsOverviewData = data as GetUserPropertyOverviewResponseWebDto;

  useEffect(() => {
    if (webAnalyticsData) {
      dispatch(PropertiesActions.updateWebAnalytics(webAnalyticsData));
    }
  }, [webAnalyticsData]);

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  useEffect(() => {
    if (isPropertyDetailsOverviewLoading === false && isUseWebAnalyticsLoading === false && onLoadComplete) {
      onLoadComplete();
    }
  }, [isPropertyDetailsOverviewLoading, isUseWebAnalyticsLoading]);

  return (
    <div data-testid="DashboardPropertyCard">
      <Box css={{ display: 'flex', justifyContent: { base: 'space-between', lg: 'start' }, alignItems: 'center' }}>
        <Box css={{ display: 'flex', justifyContent: { base: 'space-between', lg: 'start' }, alignItems: 'center' }}>
          <StyledLink to={paths.propertiesPage}>
            <IconButton
              emphasis="outlined"
              shape="circle"
              icon={<IconChevronLeftFilled />}
              size="sm"
              title="Back to properties"
            />
          </StyledLink>
          <Box
            data-loading={isPropertyDetailsOverviewLoading || null}
            css={{ overflow: 'hidden', ...propertyPhotoContainerStyles }}
          >
            {!isPropertyDetailsOverviewLoading && (
              <ConstellationPropertyCard.Photo
                src={propertyDetailsOverviewData?.photoUrl}
                alt={propertyDetailsOverviewData?.photoUrl ? 'property' : 'house'}
                fallbackImg={<ConstellationPropertyCard.PhotoFallback />}
                css={{ width: '100%', height: '100%' }}
              />
            )}
          </Box>
          <Box
            css={{
              display: 'flex',
              flexDirection: { base: 'column', lg: 'row' },
              alignSelf: { base: 'flex-start', lg: 'center' },
            }}
          >
            <AddressDetails address={propertyDetailsOverviewData?.address} />
            <Box
              css={{
                alignSelf: 'start',
                lg: { marginTop: '300' },
                xl: { marginTop: '100' },
              }}
            >
              <LoadingTag
                size={tagSize}
                tone={
                  isPropertyDetailsOverviewLoading
                    ? 'gray'
                    : getTagTypeByKey(STATUS_KEY?.PROPERTY_DETAILS_OVERVIEW, propertyDetailsOverviewData?.status)
                }
                icon={
                  Boolean(propertyDetailsOverviewData?.status === 'UNABLE TO PUBLISH') && (
                    <Icon size="md" css={{ color: 'icon.feedback.notify.hero' }} render={<IconWarningFilled />} />
                  )
                }
                isLoading={isPropertyDetailsOverviewLoading}
                loadingWidth="5rem"
              >
                {propertyDetailsOverviewData?.status}
              </LoadingTag>
            </Box>
          </Box>
        </Box>
        <Box
          css={{
            alignSelf: 'start',
            marginLeft: '200',
            lg: { marginLeft: '400', paddingTop: '18px' },
          }}
        >
          <EditPropertyTypeMenu />
        </Box>
      </Box>
    </div>
  );
};

export default memo(PropertyCard);
